import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Paper,
  Fade,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
} from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';

interface BackgroundSelectorProps {
  selectedBackground: string;
  backgroundImages: Array<{ id: string; url: string; name: string }>;
  onBackgroundChange: (bgId: string) => void;
  fallbackGradients?: { [key: string]: string };
}

const defaultFallbacks: { [key: string]: string } = {
  '1': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  '2': 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
  '3': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
};

export const BackgroundSelector: React.FC<BackgroundSelectorProps> = ({
  selectedBackground,
  backgroundImages,
  onBackgroundChange,
  fallbackGradients = defaultFallbacks,
}) => {
  const [showSelector, setShowSelector] = useState(false);
  const theme = useTheme();
  const panelId = 'background-selector-panel';

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 24,
        right: 24,
        zIndex: 10,
      }}
    >
      <Tooltip title="Cambiar fondo">
        <IconButton
          onClick={() => setShowSelector(!showSelector)}
          aria-label="Cambiar fondo"
          aria-expanded={showSelector}
          aria-controls={panelId}
          sx={{
            bgcolor:
              theme.palette.mode === 'dark'
                ? 'rgba(15, 23, 36, 0.7)'
                : 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              bgcolor:
                theme.palette.mode === 'dark'
                  ? 'rgba(15, 23, 36, 0.9)'
                  : 'rgba(255, 255, 255, 1)',
            },
          }}
        >
          <ImageIcon />
        </IconButton>
      </Tooltip>
      
      {showSelector && (
        <Fade in={showSelector}>
          <Paper
            id={panelId}
            role="menu"
            aria-label="Selector de fondo"
            sx={{
              position: 'absolute',
              top: 56,
              right: 0,
              p: 2,
              bgcolor:
                theme.palette.mode === 'dark'
                  ? theme.palette.background.paper
                  : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              minWidth: 200,
              boxShadow: 3,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Seleccionar Fondo
            </Typography>
            <List disablePadding>
              {backgroundImages.map((bg) => {
                const isSelected = selectedBackground === bg.id;
                return (
                  <ListItemButton
                    key={bg.id}
                    role="menuitemradio"
                    aria-checked={isSelected}
                    selected={isSelected}
                    onClick={() => {
                      onBackgroundChange(bg.id);
                      setShowSelector(false);
                    }}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&:last-of-type': { mb: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 32,
                        borderRadius: 1,
                        backgroundImage: `url(${bg.url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundColor: fallbackGradients[bg.id] || theme.palette.divider,
                        border: '2px solid',
                        borderColor: isSelected ? theme.palette.primary.main : 'transparent',
                        mr: 1.5,
                      }}
                    />
                    <ListItemText
                      primaryTypographyProps={{ variant: 'body2' }}
                      primary={bg.name}
                      secondary={
                        fallbackGradients[bg.id]
                          ? 'Gradiente corporativo'
                          : undefined
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

